<template>
  <div class="json-node">
    <div class="json-row">
      <button
        v-if="isCollapsible"
        class="toggle-button"
        type="button"
        @click="toggle"
      >
        {{ expanded ? "▾" : "▸" }}
      </button>
      <span v-else class="toggle-placeholder"></span>

      <span v-if="showLabel" class="node-key">"{{ label }}"</span>
      <span v-if="showLabel" class="node-separator">:</span>

      <template v-if="isCollapsible">
        <span class="node-bracket">{{ openBracket }}</span>
        <span v-if="!expanded" class="node-summary">{{ summary }}</span>
        <span v-if="!expanded" class="node-bracket">{{ closeBracket }}</span>
      </template>

      <template v-else>
        <span class="node-value" :class="valueClass">{{ formattedValue }}</span>
      </template>
    </div>

    <div v-if="isCollapsible && expanded" class="json-children">
      <JsonTreeNode
        v-for="child in childNodes"
        :key="child.key"
        :label="child.label"
        :value="child.value"
      />
      <div class="json-row">
        <span class="toggle-placeholder"></span>
        <span class="node-bracket">{{ closeBracket }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "JsonTreeNode",
  props: {
    label: {
      type: [String, Number],
      default: "",
    },
    value: {
      type: null,
      required: true,
    },
  },
  data() {
    return {
      expanded: true,
    };
  },
  computed: {
    isArray() {
      return Array.isArray(this.value);
    },
    isObject() {
      return this.value !== null && typeof this.value === "object";
    },
    showLabel() {
      return this.label !== "" && this.label !== null && this.label !== undefined && !this.isArrayIndex;
    },
    isArrayIndex() {
      return typeof this.label === "number";
    },
    isCollapsible() {
      return this.isObject;
    },
    openBracket() {
      return this.isArray ? "[" : "{";
    },
    closeBracket() {
      return this.isArray ? "]" : "}";
    },
    childNodes() {
      if (!this.isCollapsible) {
        return [];
      }
      if (this.isArray) {
        return this.value.map((item, index) => ({
          key: `array-${index}`,
          label: index,
          value: item,
        }));
      }
      return Object.keys(this.value).map((key) => ({
        key,
        label: key,
        value: this.value[key],
      }));
    },
    summary() {
      return this.isArray ? ` ${this.value.length} items ` : ` ${Object.keys(this.value).length} keys `;
    },
    formattedValue() {
      if (typeof this.value === "string") {
        return `"${this.value}"`;
      }
      if (this.value === null) {
        return "null";
      }
      return String(this.value);
    },
    valueClass() {
      if (typeof this.value === "string") {
        return "is-string";
      }
      if (typeof this.value === "number") {
        return "is-number";
      }
      if (typeof this.value === "boolean") {
        return "is-boolean";
      }
      if (this.value === null) {
        return "is-null";
      }
      return "";
    },
  },
  methods: {
    toggle() {
      this.expanded = !this.expanded;
    },
  },
};
</script>

<style scoped>
.json-node {
  font-family: "SFMono-Regular", "Consolas", "Liberation Mono", monospace;
  font-size: 13px;
  line-height: 1.7;
}

.json-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.json-children {
  padding-left: 18px;
}

.toggle-button,
.toggle-placeholder {
  width: 16px;
  flex: 0 0 16px;
}

.toggle-button {
  border: 0;
  padding: 0;
  background: transparent;
  color: #914123;
  cursor: pointer;
  line-height: 1.2;
}

.node-key {
  color: #914123;
}

.node-separator,
.node-bracket {
  color: #7b6855;
}

.node-summary {
  color: #7b6855;
}

.node-value.is-string {
  color: #2f7a4f;
}

.node-value.is-number {
  color: #b85a2a;
}

.node-value.is-boolean {
  color: #7f4bb3;
}

.node-value.is-null {
  color: #9b8d7d;
}
</style>
